import { Link, useLocation } from 'react-router-dom';
import { Plane, Bus, Clock, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { logRedirect } from '../lib/api';

export const PackageCard = ({ package: pkg, className = '', useSeoUrl = false }) => {
  const location = useLocation();

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const trackViewDetail = () => {
    fetch(`${API_URL}/api/track/view-detail?package_id=${encodeURIComponent(pkg.id)}&package_title=${encodeURIComponent(pkg.title)}`, { method: 'POST' }).catch(() => {});
  };

  // Generate package URL - SEO friendly if available
  const getPackageUrl = () => {
    if (useSeoUrl && pkg.slug && pkg.destination_slug) {
      return `/oferte/${pkg.destination_slug}/${pkg.slug}`;
    }
    // Fallback to legacy URL
    return `/pachet/${pkg.id}`;
  };

  // Check operator types
  const isPerfectTour = pkg.operator?.toLowerCase() === 'perfect tour';
  const isParalela45 = pkg.operator?.toLowerCase() === 'paralela 45';
  const isCircuite = pkg.category?.toLowerCase() === 'circuite' || 
                     pkg.category?.toLowerCase() === 'circuite externe' ||
                     pkg.categories?.some(c => c.toLowerCase().includes('circuite'));
  const isPerfectTourCircuite = isPerfectTour && isCircuite;

  // Calculate discount percentage
  let discountPercent = null;
  if (pkg.old_price && pkg.price) {
    const oldNums = String(pkg.old_price).match(/[\d.,]+/);
    const newNums = String(pkg.price).match(/[\d.,]+/);
    if (oldNums && newNums) {
      const oldVal = parseFloat(oldNums[0].replace(',', '.'));
      const newVal = parseFloat(newNums[0].replace(',', '.'));
      if (oldVal > 0 && newVal < oldVal) {
        const pct = Math.round((1 - newVal / oldVal) * 100);
        if (pct >= 15) discountPercent = pct;
      }
    }
  }

  // Detect "Nou" — created in last 7 days
  const isNew = (() => {
    if (!pkg.created_at) return false;
    const created = new Date(pkg.created_at);
    if (isNaN(created.getTime())) return false;
    const days = (Date.now() - created.getTime()) / (1000 * 60 * 60 * 24);
    return days <= 7;
  })();

  // Popular flag from backend (top 10% by view_count)
  const isPopular = !!pkg.is_popular;

  const handleReserve = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Determine source page from current location
    const sourcePage = location.pathname === '/' 
      ? 'homepage' 
      : location.pathname.replace('/', '').replace(/\//g, '-') || 'homepage';
    
    try {
      await logRedirect(pkg.id, pkg.title, pkg.operator, pkg.category, sourcePage);
      window.open(pkg.redirect_link, '_blank');
    } catch (error) {
      // Still redirect even if logging fails
      window.open(pkg.redirect_link, '_blank');
    }
  };

  // Handler for Perfect Tour Circuite "Vezi detalii" - opens redirect_link in new tab
  const handlePerfectTourDetails = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const sourcePage = location.pathname === '/' 
      ? 'homepage' 
      : location.pathname.replace('/', '').replace(/\//g, '-') || 'homepage';
    
    try {
      await logRedirect(pkg.id, pkg.title, pkg.operator, pkg.category, sourcePage);
      window.open(pkg.redirect_link, '_blank');
    } catch (error) {
      // Still redirect even if logging fails
      window.open(pkg.redirect_link, '_blank');
    }
  };

  // Get redirect message - generic for all operators
  const getRedirectMessage = () => {
    return 'Veți fi redirecționat către site-ul comerciantului';
  };

  return (
    <div 
      className={`glass-card rounded-xl overflow-hidden group hover:scale-[1.02] hover:shadow-[0_0_8px_rgba(212,175,55,0.2)] transition-all duration-300 flex flex-col h-full ${className}`}
      data-testid={`package-card-${pkg.id}`}
    >
      {/* Image Container */}
      <div className="relative h-36 overflow-hidden">
        <img
          src={pkg.image_url}
          alt={pkg.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Operator Badge - Top Right */}
        <div 
          className="absolute top-3 right-3 z-10 px-3 py-1 rounded-lg text-xs font-semibold text-white"
          style={{ background: 'rgba(30, 30, 30, 0.75)' }}
          data-testid={`operator-badge-${pkg.id}`}
        >
          {pkg.operator}
        </div>

        {/* Badges - Top Left */}
        <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
          {discountPercent && (
            <span className="bg-red-500 px-2.5 py-1 rounded-full text-xs font-bold text-white shadow-lg" data-testid={`discount-badge-${pkg.id}`}>
              -{discountPercent}%
            </span>
          )}
          {isPopular && (
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 px-2.5 py-1 rounded-full text-xs font-bold text-white shadow-lg flex items-center gap-1" data-testid={`popular-badge-${pkg.id}`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              Popular
            </span>
          )}
          {isNew && (
            <span className="bg-emerald-500 px-2.5 py-1 rounded-full text-xs font-bold text-white shadow-lg" data-testid={`new-badge-${pkg.id}`}>
              Nou
            </span>
          )}
          {pkg.is_early_booking && (
            <span className="badge-early-booking px-2.5 py-1 rounded-full text-xs font-semibold text-white">
              Early Booking
            </span>
          )}
          {pkg.is_last_minute && (
            <span className="badge-last-minute px-2.5 py-1 rounded-full text-xs font-semibold text-white">
              Last Minute
            </span>
          )}
        </div>

        {/* Transport Icon - Bottom Left (hidden for concerts) */}
        {pkg.category !== 'Concerte' && (
        <div className="absolute bottom-3 left-3 z-10">
          <div className="glass px-2 py-1 rounded-lg flex items-center gap-1">
            {pkg.transport_type === 'avion' ? (
              <Plane className="w-3.5 h-3.5 text-gold" />
            ) : pkg.transport_type === 'individual' ? (
              <User className="w-3.5 h-3.5 text-gold" />
            ) : (
              <Bus className="w-3.5 h-3.5 text-gold" />
            )}
            <span className="text-xs text-white/80">
              {pkg.transport_type === 'avion' ? 'Avion' : pkg.transport_type === 'individual' ? 'Individual' : 'Autocar'}
            </span>
          </div>
        </div>
        )}

        {/* Concert badge - Bottom Left */}
        {pkg.category === 'Concerte' && pkg.music_genre && (
        <div className="absolute bottom-3 left-3 z-10">
          <div className="px-2 py-1 rounded-lg bg-purple-600/80 backdrop-blur-sm">
            <span className="text-xs text-white font-medium">{pkg.music_genre}</span>
          </div>
        </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col flex-grow">
        {/* Duration */}
        <div className="flex items-center gap-2 mb-1.5">
          <div className="flex items-center gap-1 text-white/60 text-xs">
            <Clock className="w-3 h-3" />
            {pkg.duration}
          </div>
        </div>

        {/* Title */}
        <h3 className="font-heading font-semibold text-white text-sm mb-2 line-clamp-2 group-hover:text-gold transition-colors">
          {pkg.title}
        </h3>

        {/* Price */}
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-white/50 text-xs uppercase tracking-wide">de la</span>
          {pkg.old_price && (
            <span className="text-white/40 line-through text-sm">
              {pkg.old_price}
            </span>
          )}
          <span className="text-gold font-bold text-xl">
            {pkg.price}
          </span>
          <span className="text-white/40 text-xs">/pers</span>
        </div>

        {/* Spacer to push buttons to bottom */}
        <div className="flex-grow"></div>

        {/* Buttons - Consistent styling for all operators */}
        {isPerfectTourCircuite || isPerfectTour ? (
          /* Perfect Tour: Single "Vezi detalii" button that opens redirect_link in new tab */
          <div className="flex flex-col items-center">
            <Button
              onClick={() => { trackViewDetail(); handlePerfectTourDetails(); }}
              variant="outline"
              className="w-full border-navy bg-white/5 text-white hover:bg-gold hover:text-navy hover:border-gold transition-all duration-300 text-sm py-2.5 font-medium"
              data-testid={`details-btn-${pkg.id}`}
            >
              Vezi detalii
            </Button>
            <p className="text-white/50 text-[10px] italic mt-1.5 text-center">
              {getRedirectMessage()}
            </p>
          </div>
        ) : (
          /* All operators: Two buttons - Vezi Detalii (internal) + Rezervă (external) + redirect message */
          <div className="flex flex-col">
            <div className="flex gap-2">
              <Link 
                to={getPackageUrl()}
                className="flex-1"
                onClick={trackViewDetail}
              >
                <Button
                  variant="outline"
                  className="w-full border-navy bg-white/5 text-white hover:bg-gold hover:text-navy hover:border-gold transition-all duration-300 text-sm py-2.5 font-medium"
                  data-testid={`details-btn-${pkg.id}`}
                >
                  Vezi Detalii
                </Button>
              </Link>
              <Button
                onClick={handleReserve}
                className="flex-1 bg-gold hover:bg-gold/90 text-navy font-medium text-sm py-2.5 transition-all duration-300"
                data-testid={`reserve-btn-${pkg.id}`}
              >
                Rezervă
              </Button>
            </div>
            <p className="text-white/50 text-[10px] italic mt-1.5 text-center">
              {getRedirectMessage()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
